import type { CSSProperties } from 'react';

/** Canonical crop/focal preview aspects — match real Bilt display surfaces @390px. */

export type CropPresetId =
  | 'avatar'
  | 'entity_logo'
  | 'event_flyer'
  | 'vehicle_card'
  | 'vehicle_hero'
  | 'maintenance_receipt'
  | 'passport_memory';

export type CropPreset = {
  id: CropPresetId;
  label: string;
  /** width / height */
  aspect: number;
  shape: 'circle' | 'square' | 'rounded';
  /** crop = export cropped blob; focal = pick focal point, upload full frame */
  mode: 'crop' | 'focal';
  hint?: string;
};

export const IMAGE_CROP_PRESETS: Record<CropPresetId, CropPreset> = {
  avatar: {
    id: 'avatar',
    label: 'Profile photo',
    aspect: 1,
    shape: 'circle',
    mode: 'crop',
    hint: 'Drag and zoom so your face fills the circle.',
  },
  entity_logo: {
    id: 'entity_logo',
    label: 'Logo',
    aspect: 1,
    shape: 'square',
    mode: 'crop',
    hint: 'Center the mark. Transparent edges are trimmed on upload.',
  },
  event_flyer: {
    id: 'event_flyer',
    label: 'Event flyer',
    aspect: 16 / 10,
    shape: 'rounded',
    mode: 'crop',
    hint: 'This matches the public event header crop.',
  },
  vehicle_card: {
    id: 'vehicle_card',
    label: 'Discover card',
    aspect: 2,
    shape: 'rounded',
    mode: 'focal',
    hint: 'Choose what stays visible on build cards and lists.',
  },
  vehicle_hero: {
    id: 'vehicle_hero',
    label: 'Build gallery',
    aspect: 16 / 9,
    shape: 'rounded',
    mode: 'focal',
    hint: 'Pick the focus for your hero and gallery photos.',
  },
  maintenance_receipt: {
    id: 'maintenance_receipt',
    label: 'Receipt',
    aspect: 3 / 4,
    shape: 'rounded',
    mode: 'crop',
    hint: 'Keep text readable. Full receipt is stored without tight crop.',
  },
  passport_memory: {
    id: 'passport_memory',
    label: 'Encounter photo',
    aspect: 1,
    shape: 'rounded',
    mode: 'crop',
    hint: 'Center the moment. You can add more than one photo.',
  },
};

type PhotoNumber = number | string | null;

function finitePhotoNumber(value: PhotoNumber | undefined): number | null {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizedFocalValue(value?: PhotoNumber): number | null {
  const parsed = finitePhotoNumber(value);
  return parsed == null ? null : Math.max(0, Math.min(1, parsed));
}

export function focalToObjectPosition(focalX?: PhotoNumber, focalY?: PhotoNumber): string {
  const x = normalizedFocalValue(focalX) ?? 0.5;
  const y = normalizedFocalValue(focalY) ?? 0.5;
  return `${Math.round(x * 1000) / 10}% ${Math.round(y * 1000) / 10}%`;
}

export function normalizedPhotoZoom(zoom?: PhotoNumber): number {
  const parsed = finitePhotoNumber(zoom);
  return parsed == null ? 1 : Math.max(1, Math.min(3, parsed));
}

/** Every photo surface uses this same cover, position and scale model. */
export function framedPhotoStyle(
  focalX?: PhotoNumber,
  focalY?: PhotoNumber,
  zoom?: PhotoNumber
): CSSProperties {
  const position = focalToObjectPosition(focalX, focalY);
  return {
    objectFit: 'cover',
    objectPosition: position,
    transform: `scale(${normalizedPhotoZoom(zoom)})`,
    transformOrigin: position,
  };
}

export function normalizeFocalInput(x?: PhotoNumber, y?: PhotoNumber): { x: number; y: number } {
  return {
    x: normalizedFocalValue(x) ?? 0.5,
    y: normalizedFocalValue(y) ?? 0.5,
  };
}
